import { useState } from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Property, QuoteResponse } from '../types';
import { getGroupedCountryOptions } from '../lib/countries';
import './GuestInfoPage.css';

interface BookingState {
  property?: Property;
  checkin?: string;
  checkout?: string;
  guestCount?: number;
  quote?: QuoteResponse;
  couponCode?: string;
}

function GuestInfoPage() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const state = (location.state || {}) as BookingState;

  const { property, checkin, checkout, guestCount } = state;
  // 予約作成時に料金・クーポンが変わっていたら（409）、サーバーが返した最新見積もりに差し替える
  const [quoteOverride, setQuoteOverride] = useState<QuoteResponse | null>(null);
  const [couponDropped, setCouponDropped] = useState(false);
  const [priceNotice, setPriceNotice] = useState<string | null>(null);
  const quote = quoteOverride ?? state.quote;
  const couponCode = couponDropped ? undefined : state.couponCode;

  // 言語別の選択可能な支払方法（value は Stripe に渡す method 識別子）
  const paymentMethodsByLocale: Record<string, { value: string; label: string }[]> = {
    ja: [
      { value: 'card', label: '💳 Visa / Mastercard / JCB / Amex' },
      { value: 'apple_pay', label: ' Apple Pay' },
      { value: 'google_pay', label: 'G Google Pay' },
      { value: 'paypay', label: '🟢 PayPay' },
    ],
    en: [
      { value: 'card', label: '💳 Credit / Debit Card' },
      { value: 'apple_pay', label: ' Apple Pay' },
      { value: 'google_pay', label: 'G Google Pay' },
    ],
    zh: [
      { value: 'card', label: '💳 Visa / Mastercard / JCB' },
      { value: 'alipay', label: '💙 Alipay' },
      { value: 'wechat_pay', label: '💚 WeChat Pay' },
      { value: 'apple_pay', label: ' Apple Pay' },
    ],
  };
  const currentLocale = ['ja', 'en', 'zh'].includes(i18n.language)
    ? i18n.language
    : 'en';
  const paymentMethods = paymentMethodsByLocale[currentLocale];

  // 居住国リスト: 主要市場を上部に固定し、その下に全世界を国名順で表示（すぐ選べるように）。
  const countryGroups = getGroupedCountryOptions(currentLocale, {
    priority: t('booking.countryGroupMajor'),
    others: t('booking.countryGroupOthers'),
  });

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [country, setCountry] = useState('');
  const [address, setAddress] = useState('');

  // 会員関連
  const [wantRegister, setWantRegister] = useState(false); // メンバー登録する
  const [memberPassword, setMemberPassword] = useState(''); // 登録時のパスワード
  const [loggedInUser, setLoggedInUser] = useState<{ id: string; email: string; name: string } | null>(null);
  const [showLogin, setShowLogin] = useState(false);        // ログインモーダル表示
  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [loginError, setLoginError] = useState<string | null>(null);
  const [loginLoading, setLoginLoading] = useState(false);

  const [selectedMethod, setSelectedMethod] = useState<string>('card');
  const [agreed, setAgreed] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [touched, setTouched] = useState(false);

  // 直接アクセス等でstateが無い場合は予約画面へ戻す
  if (!property || !checkin || !checkout || !quote) {
    return (
      <div className="guest-page">
        <div className="container guest-container">
          <p className="guest-empty-note">{t('booking.selectDates')}</p>
          <Link to="/booking" className="btn btn-outline">
            {t('common.back')}
          </Link>
        </div>
      </div>
    );
  }

  const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  const nameValid = name.trim().length > 0;
  const countryValid = country.trim().length > 0;
  // メンバー登録する場合はパスワード8文字以上が必須（ログイン済みなら不要）
  const memberPasswordValid = !wantRegister || loggedInUser != null || memberPassword.length >= 8;
  const canSubmit = nameValid && emailValid && countryValid && memberPasswordValid && agreed && !submitting;

  const fmtMoney = (n: number, currency = 'JPY') =>
    new Intl.NumberFormat('ja-JP', {
      style: 'currency',
      currency,
      maximumFractionDigits: currency === 'JPY' ? 0 : 2,
    }).format(n);

  // ログイン処理: 成功したらフォームに登録情報を自動入力
  const handleLogin = async () => {
    setLoginError(null);
    if (!loginEmail.trim() || !loginPassword) {
      setLoginError(t('booking.loginRequired'));
      return;
    }
    setLoginLoading(true);
    try {
      const res = await fetch('/api/auth/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ email: loginEmail.trim(), password: loginPassword }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.error || t('booking.loginFailed'));

      // 認証は HttpOnly Cookie（member_session）でサーバーが確立済み。保存は不要。
      const u = data.user;
      setLoggedInUser({ id: u.id, email: u.email, name: u.name });

      // フォームへ自動入力
      setName(u.name || '');
      setEmail(u.email || '');
      setPhone(u.phone || '');
      setCountry(u.country || '');
      setAddress(u.address || '');

      setShowLogin(false);
      setLoginPassword('');
      setWantRegister(false); // ログイン済みなら新規登録は不要
    } catch (err) {
      setLoginError(err instanceof Error ? err.message : t('booking.loginFailed'));
    } finally {
      setLoginLoading(false);
    }
  };

  const handleSubmit = async () => {
    setTouched(true);
    if (!canSubmit || submitting) return;  // 二重送信防止
    setSubmitting(true);
    setError(null);

    try {
      // ⓪ 「メンバー登録する」がチェックされ、まだログインしていなければ会員登録する
      let memberUserId: string | undefined = loggedInUser?.id;
      if (wantRegister && !loggedInUser) {
        const regRes = await fetch('/api/auth/register', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            email: email.trim(),
            password: memberPassword,
            name: name.trim(),
            phone: phone.trim() || undefined,
            country: country || undefined,
            address: address.trim() || undefined,
            locale: i18n.language,
          }),
        });
        const regData = await regRes.json();
        if (!regRes.ok) {
          throw new Error(regData?.error || t('booking.registerFailed'));
        }
        // 認証は HttpOnly Cookie でサーバーが確立済み。保存は不要。
        memberUserId = regData.user?.id;
        setLoggedInUser({ id: regData.user.id, email: regData.user.email, name: regData.user.name });
      }

      // ① 予約を作成（仮押さえ）
      const createRes = await fetch('/api/reservations', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          property_id: property.id,
          checkin_date: checkin,
          checkout_date: checkout,
          guest_count: guestCount,
          guest_name: name.trim(),
          guest_email: email.trim(),
          guest_phone: phone.trim() || undefined,
          guest_country: country || undefined,
          guest_address: address.trim() || undefined,
          guest_locale: i18n.language,
          user_id: memberUserId,
          // すべてStripe Checkout経由のため payment_method は 'card'（=Stripe決済）。
          // ゲストが選んだ具体的手段は preferred_payment_method として渡す（記録用）。
          payment_method: 'card',
          preferred_payment_method: selectedMethod,
          // クーポン（サーバーで再検証）と、画面に表示している合計（違えば 409 で再確認）
          coupon_code: couponCode,
          expected_total_price: quote.total_price,
        }),
      });
      const created = await createRes.json();
      if (createRes.status === 409 && (created?.error === 'COUPON_CHANGED' || created?.error === 'PRICE_CHANGED')) {
        // 予約は作られていない。最新料金を表示して、もう一度押してもらう。
        if (created.error === 'COUPON_CHANGED') setCouponDropped(true);
        if (created.quote) setQuoteOverride(created.quote as QuoteResponse);
        setPriceNotice(t(created.error === 'COUPON_CHANGED' ? 'booking.couponChanged' : 'booking.priceChanged'));
        setSubmitting(false);
        window.scrollTo({ top: 0, behavior: 'smooth' });
        return;
      }
      if (!createRes.ok) {
        throw new Error(created?.error || t('booking.createFailed'));
      }

      const reservationId: string = created.reservation_id;

      // ② Stripe Checkout セッションを発行してリダイレクト
      // 戻り先はこのページを開いている origin を基準にする。
      // これにより本番ドメインでもローカル開発（localhost）でも、
      // 決済後は「今いる場所」に戻る（API側の SITE_DOMAIN 固定より優先される）。
      // 戻り先URLに現在の言語を ?lng= で付与する。Stripe Checkout の外部遷移で
      // i18n の言語状態がリセットされ、完了画面が既定の日本語に戻るのを防ぐ。
      const origin = window.location.origin;
      const successUrl =
        `${origin}/booking/complete?reservation_id=${reservationId}` +
        `&email=${encodeURIComponent(email.trim())}` +
        `&session_id={CHECKOUT_SESSION_ID}` +
        `&lng=${currentLocale}`;
      const cancelUrl = `${origin}/booking/cancel?reservation_id=${reservationId}&lng=${currentLocale}`;

      const checkoutRes = await fetch(
        `/api/reservations/${reservationId}/checkout`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            success_url: successUrl,
            cancel_url: cancelUrl,
            preferred_payment_method: selectedMethod,
          }),
        }
      );
      const checkoutData = await checkoutRes.json();
      if (!checkoutRes.ok || !checkoutData?.checkout_url) {
        throw new Error(checkoutData?.error || t('booking.createFailed'));
      }

      // Stripe の決済ページへ移動
      window.location.href = checkoutData.checkout_url;
    } catch (err) {
      setError(err instanceof Error ? err.message : t('booking.createFailed'));
      setSubmitting(false);
    }
  };

  const nights = quote.nights;

  return (
    <div className="guest-page">
      <div className="container guest-container">
        <h1 className="guest-title">{t('booking.guestStep')}</h1>
        {priceNotice && <div className="price-notice" role="alert">{priceNotice}</div>}

        <div className="guest-layout">
          {/* 左: 入力フォーム */}
          <div className="guest-form-col">
            {/* お客様情報 */}
            <section className="guest-card">
              <div className="guest-heading-row">
                <h2 className="guest-heading">{t('booking.guestInfo')}</h2>
                {loggedInUser ? (
                  <span className="member-badge">
                    {t('booking.loggedInAs', { name: loggedInUser.name })}
                  </span>
                ) : (
                  <button
                    type="button"
                    className="member-login-btn"
                    onClick={() => { setLoginError(null); setShowLogin(true); }}
                  >
                    {t('booking.memberLogin')}
                  </button>
                )}
              </div>

              <label className="field">
                <span className="field-label">
                  {t('booking.name')}
                  <em className="req">{t('booking.requiredMark')}</em>
                </span>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder={t('booking.namePlaceholder')}
                  className={touched && !nameValid ? 'invalid' : ''}
                />
                {touched && !nameValid && (
                  <span className="field-error">{t('booking.nameWarning')}</span>
                )}
              </label>

              <label className="field">
                <span className="field-label">
                  {t('booking.email')}
                  <em className="req">{t('booking.requiredMark')}</em>
                </span>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={t('booking.emailPlaceholder')}
                  className={touched && !emailValid ? 'invalid' : ''}
                />
                {touched && !emailValid && (
                  <span className="field-error">{t('booking.emailWarning')}</span>
                )}
              </label>

              <label className="field">
                <span className="field-label">{t('booking.phone')}</span>
                <input
                  type="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder={t('booking.phonePlaceholder')}
                />
              </label>

              <label className="field">
                <span className="field-label">
                  {t('booking.country')}
                  <em className="req">{t('booking.requiredMark')}</em>
                </span>
                <select
                  value={country}
                  onChange={(e) => setCountry(e.target.value)}
                  className={touched && !countryValid ? 'invalid' : ''}
                >
                  <option value="">{t('booking.countryPlaceholder')}</option>
                  {/* 旧データ（国コードに一致しない既存値）が入っていた場合も失わず表示する */}
                  {country && !countryGroups.some((g) => g.options.some((o) => o.code === country)) && (
                    <option value={country}>{country}</option>
                  )}
                  {countryGroups.map((g) => (
                    <optgroup key={g.label} label={g.label}>
                      {g.options.map((o) => (
                        <option key={o.code} value={o.code}>{o.label}</option>
                      ))}
                    </optgroup>
                  ))}
                </select>
                {touched && !countryValid && (
                  <span className="field-error">{t('booking.countryWarning')}</span>
                )}
              </label>

              <label className="field">
                <span className="field-label">{t('booking.address')}</span>
                <input
                  type="text"
                  value={address}
                  onChange={(e) => setAddress(e.target.value)}
                  placeholder={t('booking.addressPlaceholder')}
                />
              </label>

              {/* メンバー登録（ログイン済みなら非表示） */}
              {!loggedInUser && (
                <div className="member-register">
                  <label className="member-check">
                    <input
                      type="checkbox"
                      checked={wantRegister}
                      onChange={(e) => setWantRegister(e.target.checked)}
                    />
                    <span>{t('booking.registerMember')}</span>
                  </label>
                  {wantRegister && (
                    <label className="field">
                      <span className="field-label">
                        {t('booking.memberPassword')}
                        <em className="req">{t('booking.requiredMark')}</em>
                      </span>
                      <input
                        type="password"
                        value={memberPassword}
                        onChange={(e) => setMemberPassword(e.target.value)}
                        placeholder={t('booking.memberPasswordPlaceholder')}
                        className={touched && wantRegister && memberPassword.length < 8 ? 'invalid' : ''}
                      />
                      {touched && wantRegister && memberPassword.length < 8 && (
                        <span className="field-error">{t('booking.memberPasswordWarning')}</span>
                      )}
                      <span className="member-hint">{t('booking.registerMemberHint')}</span>
                    </label>
                  )}
                </div>
              )}
            </section>

            {/* 利用規約・キャンセルポリシー */}
            <section className="guest-card">
              <h2 className="guest-heading">{t('booking.termsHeading')}</h2>
              <p className="terms-note">{t('booking.cancelPolicyNote')}</p>
              <div className="terms-box">
                <ul>
                  {(t('booking.cancelPolicyItems', { returnObjects: true }) as string[]).map((item, i) => (
                    <li key={i}>{item}</li>
                  ))}
                </ul>
              </div>
              <label className="agree-check">
                <input
                  type="checkbox"
                  checked={agreed}
                  onChange={(e) => setAgreed(e.target.checked)}
                />
                <span>{t('booking.agreeTerms')}</span>
              </label>
            </section>

            {error && <p className="submit-error">{error}</p>}

            {/* 対応決済手段（言語別に表示。実際の課金手段はStripeが自動判定） */}
            {/* 支払方法（複数から1つを選択） */}
            <div className="payment-methods">
              <span className="pm-heading">{t('booking.paymentMethod')}</span>
              <div className="pm-list">
                {paymentMethods.map((m) => (
                  <label
                    key={m.value}
                    className={`pm-option ${
                      selectedMethod === m.value ? 'selected' : ''
                    }`}
                  >
                    <input
                      type="radio"
                      name="payment_method"
                      value={m.value}
                      checked={selectedMethod === m.value}
                      onChange={() => setSelectedMethod(m.value)}
                    />
                    <span className="pm-option-label">{m.label}</span>
                  </label>
                ))}
              </div>
            </div>

            <div className="guest-actions">
              <button
                className="btn btn-outline"
                onClick={() => navigate('/booking')}
                disabled={submitting}
              >
                {t('booking.backToDates')}
              </button>
              <button
                className="btn btn-pay"
                onClick={handleSubmit}
                disabled={!canSubmit}
              >
                {submitting
                  ? t('booking.redirecting')
                  : t('booking.payButton')}
              </button>
            </div>
          </div>

          {/* 右: 予約サマリ */}
          <aside className="summary-col">
            <div className="summary-card">
              <h2 className="guest-heading">{t('booking.bookingSummary')}</h2>
              <div className="summary-prop">{property.name}</div>
              <div className="summary-row">
                <span>{t('confirmation.checkinLabel')}</span>
                <span>{checkin}</span>
              </div>
              <div className="summary-row">
                <span>{t('confirmation.checkoutLabel')}</span>
                <span>{checkout}</span>
              </div>
              <div className="summary-row">
                <span>{t('confirmation.guestCountLabel')}</span>
                <span>
                  {guestCount} {t('booking.guests')} / {nights}{' '}
                  {t('booking.nights')}
                </span>
              </div>

              <div className="summary-divider" />

              <div className="summary-row">
                <span>{t('booking.baseAmount')}</span>
                <span>{fmtMoney(quote.base_amount, quote.currency)}</span>
              </div>
              {quote.extra_guest_amount > 0 && (
                <div className="summary-row">
                  <span>{t('booking.extraGuestAmount')}</span>
                  <span>
                    {fmtMoney(quote.extra_guest_amount, quote.currency)}
                  </span>
                </div>
              )}
              <div className="summary-row">
                <span>{t('booking.cleaningAmount')}</span>
                <span>{fmtMoney(quote.cleaning_amount, quote.currency)}</span>
              </div>
              {quote.discount_amount > 0 && (
                <div className="summary-row discount">
                  <span>
                    {quote.coupon?.applied && quote.coupon.rate
                      ? `${t('booking.couponDiscount', { rate: quote.coupon.rate })} ${quote.coupon.code}`
                      : t('booking.discountAmount')}
                  </span>
                  <span>
                    -{fmtMoney(quote.discount_amount, quote.currency)}
                  </span>
                </div>
              )}
              <div className="summary-row">
                <span>{t('booking.taxAmount')}</span>
                <span>{fmtMoney(quote.tax_amount, quote.currency)}</span>
              </div>

              <div className="summary-row total">
                <span>{t('booking.total')}</span>
                <span>{fmtMoney(quote.total_price, quote.currency)}</span>
              </div>
            </div>
          </aside>
        </div>
      </div>

      {/* ログインモーダル */}
      {showLogin && (
        <div className="login-overlay" onClick={() => !loginLoading && setShowLogin(false)}>
          <div className="login-modal" onClick={(e) => e.stopPropagation()}>
            <div className="login-modal-head">
              <h2>{t('booking.memberLogin')}</h2>
              <button
                type="button"
                className="login-close"
                onClick={() => setShowLogin(false)}
                disabled={loginLoading}
                aria-label="close"
              >
                ×
              </button>
            </div>
            <label className="field">
              <span className="field-label">{t('booking.email')}</span>
              <input
                type="email"
                value={loginEmail}
                onChange={(e) => setLoginEmail(e.target.value)}
                placeholder={t('booking.emailPlaceholder')}
              />
            </label>
            <label className="field">
              <span className="field-label">{t('booking.memberPassword')}</span>
              <input
                type="password"
                value={loginPassword}
                onChange={(e) => setLoginPassword(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') handleLogin(); }}
              />
            </label>
            {loginError && <p className="login-error">{loginError}</p>}
            <button
              type="button"
              className="btn btn-primary login-submit"
              onClick={handleLogin}
              disabled={loginLoading}
            >
              {loginLoading ? t('booking.loginLoading') : t('booking.memberLogin')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default GuestInfoPage;
